import React from 'react';
import { Routes, Route } from 'react-router-dom';
import Modal from 'react-modal';
import Home from './pages/Home';
import Settings from './pages/Settings';
import BenefitsList from './pages/BenefitsList';
import BenefitForm from './components/BenefitForm';
import BusinessList from './pages/BusinessList';
import BusinessForm from './components/BusinessForm';
import { ROUTES } from './config/routes';
import { ResetPassword } from './pages/ResetPassword';
import { RequestPasswordReset } from './pages/RequestPasswordReset';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { Login } from './pages/Login';
import { ProtectedRouts } from './components/ProtectedRouts';
import { AdminRouteGuard } from './components/AdminRouteGuard';
import { AuthLayout } from './components/layouts/AuthLayout';
import Transactions from './pages/Transactions';
import Insights from './pages/Insights';
import TermsOfService from './pages/TermsOfService';
import Billing from './pages/Billing';
import AdminHome from "./pages/AdminHome";
import AdminTransactions from './pages/AdminTransactions';
import AdminCommunication from "./pages/AdminCommunication";
import AdminAppPerformance from './pages/AdminAppPerformance';
import AdminTopMerchants from './pages/AdminTopMerchants';
import AdminCustomerBrackets from './pages/AdminCustomerBrackets';
import AdminAnalyticsPlus from './pages/AdminAnalyticsPlus';
import AdminActivitySearch from './pages/AdminActivitySearch';
import AdminActivityClicks from './pages/AdminActivityClicks';
import AdminZeroResultSuggestions from './pages/AdminZeroResultSuggestions';
import AdminKpiReport from './pages/KpiReport';
import AdminKpiReportV2 from './pages/KpiReportV2';
import AdminRevenueSharing from './pages/AdminRevenueSharing';
import AdminPartnerNetworkReport from './pages/PartnerNetworkReport';
import AdminManagement from './pages/AdminManagement';
import AdminSearchPreview from './pages/AdminSearchPreview';
import PartnerManagement from './pages/PartnerManagement';
import PartnerApplicationDetails from './pages/PartnerApplicationDetails';
import PartnerDetails from './pages/PartnerDetails';
import PromotionsPage from './features/business-onboarding/pages/PromotionsPage';
import EditBusinessDraftPage from './features/business-onboarding/pages/EditBusinessDraftPage';
import AdminVisa from './pages/AdminVisa';
import AdminVisaTransaction from './pages/AdminVisaTransaction';
import AdminFailedTransactions from './pages/AdminFailedTransactions';
import AdminUsers from './pages/AdminUsers';
import AdminErasures from './pages/AdminErasures';
import AdminCashbackRequests from './pages/AdminCashbackRequests';
import InvoicingDashboard from './features/invoicing/pages/InvoicingDashboard';
import UpgradeMarketingPackage from './pages/UpgradeMarketingPackage';
import AdminWebshop from './pages/AdminWebshop';
import AdminNotifications from './pages/AdminNotifications';
import AdminSpecialPartners from './pages/AdminSpecialPartners';
import AdminPromoCodes from './pages/AdminPromoCodes';
import AdminOptInOffers from './pages/AdminOptInOffers';
import AdminFeaturedPartners from './pages/AdminFeaturedPartners';
import AdminFeaturedPartnerEdit from './pages/AdminFeaturedPartnerEdit';
import AdminPopupCampaigns from './pages/AdminPopupCampaigns';
import AdminPopupCampaignEdit from './pages/AdminPopupCampaignEdit';

Modal.setAppElement("#root");

const App: React.FC = () => {
  return (
    <>
      <Routes>
        <Route element={<ProtectedRouts />}>
          <Route path={ROUTES.HOME()} element={<Home />} />
          <Route path={ROUTES.SETTINGS()} element={<Settings />} />
          <Route path={ROUTES.BENEFITS()} element={<BenefitsList />} />
          <Route path={ROUTES.BENEFITS_NEW()} element={<BenefitForm />} />
          <Route path={ROUTES.BENEFITS_ID()} element={<BenefitForm />} />
          <Route path={ROUTES.BUSINESSES()} element={<BusinessList />} />
          <Route path={ROUTES.BUSINESSES_NEW()} element={<BusinessForm />} />
          <Route path={ROUTES.BUSINESSES_ID()} element={<BusinessForm />} />
          <Route path={ROUTES.TRANSACTIONS()} element={<Transactions />} />
          <Route path={ROUTES.INSIGHTS()} element={<Insights />} />
          <Route
            path={ROUTES.TERMS_OF_SERVICE()}
            element={<TermsOfService />}
          />
          <Route path={ROUTES.BILLING()} element={<Billing />} />
          <Route path={ROUTES.UPGRADE_MARKETING_PACKAGE()} element={<UpgradeMarketingPackage />} />

          {/* Every admin route goes in here: the guard checks the sidebar permission
              for the route and refuses paths it has no rule for. */}
          <Route element={<AdminRouteGuard />}>
            <Route path={ROUTES.ADMIN_HOME()} element={<AdminHome />} />
            <Route
              path={ROUTES.ADMIN_TRANSACTIONS()}
              element={<AdminTransactions />}
            />
            <Route path={ROUTES.ADMIN_COMMUNICATION()} element={<AdminCommunication />} />
            <Route
              path={ROUTES.ADMIN_APP_PERFORMANCE()}
              element={<AdminAppPerformance />}
            />
            <Route path={ROUTES.ADMIN_TOP_MERCHANTS()} element={<AdminTopMerchants />} />
            <Route path={ROUTES.ADMIN_CUSTOMER_BRACKETS()} element={<AdminCustomerBrackets />} />
            <Route path={ROUTES.ADMIN_ANALYTICS_PLUS()} element={<AdminAnalyticsPlus />} />
            <Route path={ROUTES.ADMIN_ACTIVITY_SEARCH()} element={<AdminActivitySearch />} />
            <Route path={ROUTES.ADMIN_ACTIVITY_CLICKS()} element={<AdminActivityClicks />} />
            <Route path={ROUTES.ADMIN_ZERO_RESULT_SUGGESTIONS()} element={<AdminZeroResultSuggestions />} />
            <Route path={ROUTES.ADMIN_KPI_REPORT()} element={<AdminKpiReport />} />
            <Route path={ROUTES.ADMIN_KPI_REPORT_V2()} element={<AdminKpiReportV2 />} />
            <Route path={ROUTES.ADMIN_REVENUE_SHARING()} element={<AdminRevenueSharing />} />
            <Route path={ROUTES.ADMIN_PARTNER_NETWORK_REPORT()} element={<AdminPartnerNetworkReport />} />
            <Route path={ROUTES.ADMIN_MANAGEMENT()} element={<AdminManagement />} />
            <Route path={ROUTES.ADMIN_SEARCH_PREVIEW()} element={<AdminSearchPreview />} />
            <Route path={ROUTES.ADMIN_PARTNER_MANAGEMENT()} element={<PartnerManagement />} />
            <Route path={ROUTES.ADMIN_PARTNER_APPLICATION_DETAILS()} element={<PartnerApplicationDetails />} />
            <Route path={ROUTES.ADMIN_PARTNER_DETAILS()} element={<PartnerDetails />} />
            <Route path={ROUTES.ADMIN_VISA()} element={<AdminVisa />} />
            <Route path={ROUTES.ADMIN_VISA_TRANSACTION()} element={<AdminVisaTransaction />} />
            <Route path={ROUTES.ADMIN_FAILED_TRANSACTIONS()} element={<AdminFailedTransactions />} />
            <Route path={ROUTES.ADMIN_USERS()} element={<AdminUsers />} />
            <Route path={ROUTES.ADMIN_ERASURES()} element={<AdminErasures />} />
            <Route path={ROUTES.ADMIN_CASHBACK_REQUESTS()} element={<AdminCashbackRequests />} />
            <Route path={ROUTES.ADMIN_INVOICING()} element={<InvoicingDashboard />} />
            <Route path={ROUTES.ADMIN_WEBSHOP()} element={<AdminWebshop />} />
            <Route path={ROUTES.ADMIN_NOTIFICATIONS()} element={<AdminNotifications />} />
            <Route path={ROUTES.ADMIN_SPECIAL_PARTNERS()} element={<AdminSpecialPartners />} />
            <Route path={ROUTES.ADMIN_PROMO_CODES()} element={<AdminPromoCodes />} />
            <Route path={ROUTES.ADMIN_OPT_IN_OFFERS()} element={<AdminOptInOffers />} />
            <Route path={ROUTES.ADMIN_FEATURED_PARTNERS()} element={<AdminFeaturedPartners />} />
            <Route path={ROUTES.ADMIN_FEATURED_PARTNERS_EDIT()} element={<AdminFeaturedPartnerEdit />} />
            <Route path={ROUTES.ADMIN_POPUP_CAMPAIGNS()} element={<AdminPopupCampaigns />} />
            <Route path={ROUTES.ADMIN_POPUP_CAMPAIGN_EDIT()} element={<AdminPopupCampaignEdit />} />
          </Route>

          <Route path={ROUTES.PROMOTIONS()} element={<PromotionsPage />} />
          <Route path={ROUTES.PROMOTIONS_DRAFT_EDIT()} element={<EditBusinessDraftPage />} />
        </Route>

        <Route element={<AuthLayout />}>
          <Route path={ROUTES.LOGIN()} element={<Login />} />
          <Route
            path={ROUTES.REQUEST_PASSWORD_RESET()}
            element={<RequestPasswordReset />}
          />
          <Route path={ROUTES.RESET_PASSWORD()} element={<ResetPassword />} />
          <Route path={ROUTES.ACCEPT_INVITATION()} element={<AcceptInvitation />} />
        </Route>
      </Routes>
    </>
  );
};

export default App;
